import { onDocumentReady, requestIdleCallback } from '@theme/utilities';

/**
 * Custom Form Component
 * Handles loading of external form embeds with various loading strategies
 */
class CustomFormComponent {
  constructor(/** @type {HTMLElement} */ element) {
    this.element = element;
    this.formId = element.dataset.formId;
    this.loadingStrategy = element.dataset.loadingStrategy || 'on_visible';
    this.loadOffset = parseInt(element.dataset.loadOffset) || 100;
    this.formContainer = element.querySelector('.custom-form-block__embed');
    this.formUrl = this.formContainer?.dataset.formUrl;
    this.loaded = false;
    
    if (!this.formUrl) {
      console.warn('CustomForm: No form URL provided');
      return;
    }
    
    this.init();
  }
  
  init() {
    switch (this.loadingStrategy) {
      case 'immediate':
        this.loadImmediate();
        break;
      case 'on_visible':
        this.loadOnVisible();
        break;
      case 'on_idle':
        this.loadOnIdle();
        break;
      case 'on_interaction':
        this.loadOnInteraction();
        break;
      default:
        this.loadOnVisible();
    }
  }
  
  /**
   * Load form immediately with async loading
   */
  loadImmediate() {
    onDocumentReady(() => {
      this.loadForm();
    });
  }
  
  /**
   * Load form when it becomes visible in viewport
   */
  loadOnVisible() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      this.loadImmediate();
      return;
    }
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loaded) {
            this.loadForm();
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: `${this.loadOffset}px`
      }
    );
    
    observer.observe(this.formContainer);
  }
  
  /**
   * Load form when browser is idle
   */
  loadOnIdle() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.loadForm(), 3000);
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => this.loadForm(), 2000);
    }
  }
  
  /**
   * Load form on first user interaction
   */
  loadOnInteraction() {
    const triggerEvents = ['mouseenter', 'touchstart', 'scroll', 'keydown'];
    
    const loadOnInteraction = () => {
      this.loadForm();
      triggerEvents.forEach((event) => {
        window.removeEventListener(event, loadOnInteraction);
      });
    };
    
    triggerEvents.forEach((event) => {
      window.addEventListener(event, loadOnInteraction, { 
        once: true, 
        passive: true 
      });
    });
    
    // Fallback: load after 5 seconds if no interaction
    setTimeout(() => {
      if (!this.loaded) {
        this.loadForm();
      }
    }, 5000);
  }
  
  /**
   * Load the actual form script
   */
  loadForm() {
    if (this.loaded || !this.formContainer) return;
    
    this.loaded = true;
    
    // Clear loading text
    const loadingElement = this.formContainer.querySelector('.custom-form-block__loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
    
    // Load form as script (works for JotForm and other providers)
    this.loadScript();
  }
  
  /**
   * Load form script (works for JotForm and other providers)
   */
  loadScript() {
    // For JotForm, the script replaces itself with the form HTML
    // So we need to place the script tag directly in the container
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.formUrl;
    script.async = true;
    script.onload = () => {
      console.log('CustomForm: Form script loaded successfully');
    };
    script.onerror = () => {
      console.error('CustomForm: Failed to load form script from', this.formUrl);
    };
    
    // Clear the container and add the script
    this.formContainer.innerHTML = '';
    this.formContainer.appendChild(script);
  }
}

/**
 * Initialize all custom form components on the page
 */
function initCustomForms() {
  const formElements = document.querySelectorAll('.custom-form-block[data-form-id]');
  formElements.forEach((element) => {
    new CustomFormComponent(element);
  });
}

// Initialize when DOM is ready
onDocumentReady(initCustomForms);

export default CustomFormComponent;
